'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { BN, QUOTE_PRECISION } from '@velocity-exchange/sdk';
import React from 'react';
import useWalletIsConnected from 'src/hooks/useWalletIsConnected';
import useDateDisplayState from 'src/hooks/useDateDisplayState';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import { usePositionHistory } from 'src/hooks/positions/usePositionHistory';
import useDriftAccountsStore from 'src/stores/useDriftAccountsStore';
import useModal from 'src/components/Modals/hooks/useModal';
import { PnlModalProps } from 'src/components/Modals/PnlModal/types';
import { PORTFOLIO_HISTORY_MARKET_FILTER_ALL_OPTION } from 'src/components/PortfolioPage/PortfolioHistorySubPage/consts';
import { DataTable } from 'src/components/primitives/Table/DataTable';
import { MOBILE_TABLE_CARD_LIST_CLASSNAME } from 'src/components/Tables/cards/consts';
import { cm } from 'src/utils/classNames';
import ContentGuard from 'src/components/ContentGuard';
import {
	getPositionHistoryColumns,
	PositionHistoryTableMeta,
} from './positionHistoryColumns';
import { PositionHistoryRowData } from './types';
import { transformRecord } from './utils';
import { PositionHistoryMobileCard } from 'src/components/PositionsTable/PositionHistoryMobileCard';
import useExpandableItemList from 'src/hooks/lists/expandable/useExpandableItemList';
import ExpandAllButton from 'src/components/Buttons/ExpandAllButton';
import MobileTableCard from 'src/components/Tables/cards/MobileTableCard';

const PAGE_SIZE = 20;
const EMPTY_MESSAGE = 'No positions found';

interface PositionHistoryTableProps {
	marketFilter: string;
	emptyClassName?: string;
	tableContentWrapperClassName?: string;
	noExpandAllButton?: boolean;
}

function PositionHistoryTable({
	marketFilter,
	emptyClassName,
	tableContentWrapperClassName,
	noExpandAllButton,
}: PositionHistoryTableProps) {
	const isMobile = useIsMobileScreenSize();
	const isConnected = useWalletIsConnected();
	const [currentPage, setCurrentPage] = useState(0);
	const { data, isLoading } = usePositionHistory(currentPage);
	const { toggleDateDisplayType, displayDatesAsTimestamp } =
		useDateDisplayState();
	const { show } = useModal();

	const currentAccountPubkey = useDriftAccountsStore(
		(s) => s.getCurrentUserAccount()?.pubKey
	);

	const records = data?.records ?? [];
	const hasNextPage = !!data?.nextPage;

	useEffect(() => {
		setCurrentPage(0);
	}, [currentAccountPubkey?.toBase58()]);

	const filteredRecords = useMemo(() => {
		if (marketFilter === PORTFOLIO_HISTORY_MARKET_FILTER_ALL_OPTION.ticker) {
			return records;
		}
		return records.filter((record) => record.symbol === marketFilter);
	}, [records, marketFilter]);

	const displayData = useMemo(() => {
		return filteredRecords.map((record) =>
			transformRecord(record, currentAccountPubkey)
		);
	}, [filteredRecords, currentAccountPubkey]);

	const {
		areAllItemsExpanded,
		handleItemExpandedChange,
		handleExpandAllItems,
		handleCollapseAllItems,
	} = useExpandableItemList({
		tableId: 'position-history',
		numItems: filteredRecords.length,
	});

	const showPnlModal = useCallback(
		(row: PositionHistoryRowData) => {
			const record = filteredRecords.find(
				(r) =>
					`${r.takerOrderId}-${r.makerOrderId}-${
						currentAccountPubkey && r.taker?.equals(currentAccountPubkey)
							? 'taker'
							: 'maker'
					}` === row.id
			);
			if (!record) return;

			const existingBase = record.userExistingBaseAssetAmount;
			const baseForEntryPrice = existingBase ?? record.baseAssetAmountFilled;
			const quoteEntryAmount =
				row.entryPrice.toNum() * (baseForEntryPrice?.toNum() ?? 0);

			const pnlModalProps: PnlModalProps = {
				position: {
					baseSize: record.baseAssetAmountFilled.val,
					entryPrice: row.entryPrice.val,
					marketIndex: record.marketIndex,
					marketSymbol: row.marketName,
					direction: row.direction,
					quoteEntryAmount: new BN(
						quoteEntryAmount * QUOTE_PRECISION.toNumber(),
						QUOTE_PRECISION.toNumber()
					),
					maxMarginRatio: 0,
				},
				actualExitPrice: row.avgExitPrice.toNum(),
				pnl: row.pnl,
				triggeredFrom: 'position_history_table',
			};
			show({
				type: 'pnl_card',
				props: pnlModalProps,
			});
		},
		[filteredRecords, currentAccountPubkey, show]
	);

	const columns = useMemo(
		() =>
			getPositionHistoryColumns({
				onToggleDateDisplay: toggleDateDisplayType,
			}),
		[toggleDateDisplayType]
	);

	const tableMeta: PositionHistoryTableMeta = {
		displayDatesAsTimestamp,
		onShareClick: showPnlModal,
	};

	const handlePageChange = useCallback((pageIndex: number) => {
		setCurrentPage(pageIndex);
	}, []);

	const maxCount = (1 + currentPage) * PAGE_SIZE + (hasNextPage ? 1 : 0);

	const mobileContent = (
		<div>
			{!noExpandAllButton && (
				<div className="flex w-full items-center justify-end p-3">
					<ExpandAllButton
						areAllItemsExpanded={areAllItemsExpanded}
						totalCount={filteredRecords.length}
						onExpandAll={handleExpandAllItems}
						onCollapseAll={handleCollapseAllItems}
					/>
				</div>
			)}
			<div
				className={cm(
					MOBILE_TABLE_CARD_LIST_CLASSNAME,
					tableContentWrapperClassName
				)}
			>
				{displayData.length > 0 ? (
					displayData.map((row) => (
						<PositionHistoryMobileCard
							key={row.id}
							uimarket={row.uiMarket}
							marketName={row.marketName}
							direction={row.direction}
							pnl={row.pnl}
							pnlNotional={row.pnlNotional}
							positionPnlNotional={row.positionPnlNotional}
							feesAndFundingNotional={row.feesAndFundingNotional}
							pricePercentChangeDisplay={row.pricePercentChangeDisplay}
							entryPrice={row.entryPrice}
							avgExitPrice={row.avgExitPrice}
							date={row.date}
							displayDatesAsTimestamp={displayDatesAsTimestamp}
							onToggleDateDisplay={toggleDateDisplayType}
							onShareClick={() => showPnlModal(row)}
							baseClosedForPnl={
								filteredRecords.find(
									(r) =>
										`${r.takerOrderId}-${r.makerOrderId}-${
											currentAccountPubkey &&
											r.taker?.equals(currentAccountPubkey)
												? 'taker'
												: 'maker'
										}` === row.id
								)?.baseClosedForPnl
							}
							areAllItemsExpanded={areAllItemsExpanded}
							handleItemExpandedChange={handleItemExpandedChange}
						/>
					))
				) : (
					<MobileTableCard.EmptyPlaceholder>
						{EMPTY_MESSAGE}
					</MobileTableCard.EmptyPlaceholder>
				)}
			</div>
		</div>
	);

	const desktopContent = (
		<DataTable
			columns={columns}
			data={displayData}
			emptyMessage={EMPTY_MESSAGE}
			emptyClassName={emptyClassName}
			meta={tableMeta}
			columnPinning={{ right: ['explorer'] }}
			pagination={{
				pageIndex: currentPage,
				pageSize: PAGE_SIZE,
				totalCount: maxCount,
				onPageChange: handlePageChange,
			}}
		/>
	);

	return (
		<div className="flex h-full flex-col">
			<div
				className={cm(
					'min-h-0 flex-1 overflow-hidden rounded-b-sm sm:bg-container-bg',
					tableContentWrapperClassName
				)}
			>
				<ContentGuard requireAccount requireWallet isLoading={isLoading}>
					{isConnected && (isMobile ? mobileContent : desktopContent)}
				</ContentGuard>
			</div>
		</div>
	);
}

export default React.memo(PositionHistoryTable);
